"use client";

import { reportCaughtError } from "@/lib/error-tracking/report-caught-error";
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE_NAME,
  type Locale,
  toLocale,
} from "@/lib/i18n/locales";
import { isPortalHostname } from "@v1/backend/logic/portal/portal_host";
import { type ReactElement, useEffect, useState } from "react";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

const HOME_URL = "https://hausverwalter.ai";
const PORTAL_HOME_PATH = "/portal/home";

/** Tenants must land back in their portal, never on the vendor website. */
function resolveHomeHref(): string {
  if (typeof window === "undefined") return HOME_URL;
  const { pathname, hostname } = window.location;
  if (pathname.startsWith("/portal")) return PORTAL_HOME_PATH;
  if (isPortalHostname(hostname)) return "/";
  return HOME_URL;
}

/**
 * i18n-exempt: this screen renders when the root layout itself crashed, so
 * there is no `NextIntlClientProvider` and no message catalog to read from.
 * Copy is inlined for both locales instead — it must survive the very failure
 * that would break the normal translation path.
 */
const COPY: Readonly<
  Record<
    Locale,
    Readonly<Record<"title" | "body" | "errorId" | "home", string>>
  >
> = {
  de: {
    title: "Etwas ist schiefgelaufen",
    body: "Bitte gehe zurück zur Startseite.",
    errorId: "Fehler-ID:",
    home: "Zur Startseite",
  },
  en: {
    title: "Something went wrong",
    body: "Please return to the start page.",
    errorId: "Error ID:",
    home: "Go to start page",
  },
};

function readLocaleCookie(): Locale {
  if (typeof document === "undefined") return DEFAULT_LOCALE;
  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${LOCALE_COOKIE_NAME}=`));
  return toLocale(match?.slice(LOCALE_COOKIE_NAME.length + 1));
}

export default function GlobalError({ error }: GlobalErrorProps): ReactElement {
  // Starts at the default so server and client markup match, then corrects
  // itself once the cookie is readable.
  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);
  const [homeHref, setHomeHref] = useState<string>(HOME_URL);
  useEffect(() => {
    setLocale(readLocaleCookie());
    setHomeHref(resolveHomeHref());
  }, []);
  useEffect(() => {
    reportCaughtError(error, "global-error");
  }, [error]);

  const copy = COPY[locale];

  return (
    <html lang={locale}>
      <body className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
        <div className="flex w-full max-w-md flex-col gap-3 rounded-lg border border-border/60 bg-background p-6 shadow-lg">
          <h1 className="text-lg font-semibold text-primary/90">
            {copy.title}
          </h1>
          <p className="text-sm text-primary/70">{copy.body}</p>
          {error.digest ? (
            <p className="text-xs text-primary/50">
              {copy.errorId} {error.digest}
            </p>
          ) : null}
          <a
            href={homeHref}
            className="inline-flex h-9 items-center justify-center rounded-md bg-action-500 px-4 text-sm font-medium text-white transition-colors hover:bg-action-600"
          >
            {copy.home}
          </a>
        </div>
      </body>
    </html>
  );
}
